import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/shared/Layout';
import Login from './components/shared/Login';
import ProtectedRoute from './components/shared/ProtectedRoute';

// CRL Components
import CRLDashboard from './components/crl/Dashboard';
import LetterList from './components/crl/LetterList';
import LetterDetail from './components/crl/LetterDetail';
import CRLEmailAlerts from './components/crl/EmailAlerts';

// Insider Components
import InsiderDashboard from './components/insider/Dashboard';
import Positions from './components/insider/Positions';
import Signals from './components/insider/Signals';
import TradeHistory from './components/insider/TradeHistory';
import StrategyControls from './components/insider/StrategyControls';
import AlertSettings from './components/insider/AlertSettings';

// Admin Components
import UserManagement from './components/admin/UserManagement';
import PipelineStatus from './components/admin/PipelineStatus';
import AgentSettings from './components/admin/AgentSettings';

// Company Intelligence Components
import { CompanyList, CompanyDetail } from './components/company';

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Default redirect */}
              <Route index element={<Navigate to="/crl" replace />} />

              {/* CRL Routes */}
              <Route
                path="crl"
                element={
                  <ProtectedRoute requiredModule="crl">
                    <CRLDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="crl/letters"
                element={
                  <ProtectedRoute requiredModule="crl">
                    <LetterList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="crl/letters/:id"
                element={
                  <ProtectedRoute requiredModule="crl">
                    <LetterDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="crl/alerts"
                element={
                  <ProtectedRoute requiredModule="crl">
                    <CRLEmailAlerts />
                  </ProtectedRoute>
                }
              />

              {/* Insider Routes */}
              <Route
                path="insider"
                element={
                  <ProtectedRoute requiredModule="insider">
                    <InsiderDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="insider/positions"
                element={
                  <ProtectedRoute requiredModule="insider">
                    <Positions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="insider/signals"
                element={
                  <ProtectedRoute requiredModule="insider">
                    <Signals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="insider/trades"
                element={
                  <ProtectedRoute requiredModule="insider">
                    <TradeHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="insider/strategy"
                element={
                  <ProtectedRoute requiredModule="insider">
                    <StrategyControls />
                  </ProtectedRoute>
                }
              />
              <Route
                path="insider/alerts"
                element={
                  <ProtectedRoute requiredModule="insider">
                    <AlertSettings />
                  </ProtectedRoute>
                }
              />

              {/* Company Intelligence Routes */}
              <Route
                path="company"
                element={
                  <ProtectedRoute requiredModule="company">
                    <CompanyList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="company/:companyId"
                element={
                  <ProtectedRoute requiredModule="company">
                    <CompanyDetail />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="admin/users"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pipeline-status"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PipelineStatus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/agent-settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AgentSettings />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
